import { useEffect, useState } from 'react';
import { Check, Copy, Mail } from 'lucide-react';
import { EntityModal } from '../common/EntityModal';
import { useSendQuestionnaireLink } from '../../hooks/useQuestionnaires';

export interface SendQuestionnaireModalProps {
  open: boolean;
  onClose: () => void;
  questionnaireId: string | null;
  sentTo?: string | null;
}

/**
 * Generate the public no-login fill link, optionally emailing it to the
 * client. The link can also be copied and shared any other way.
 */
export function SendQuestionnaireModal({
  open,
  onClose,
  questionnaireId,
  sentTo,
}: SendQuestionnaireModalProps) {
  const [email, setEmail] = useState(sentTo ?? '');
  const [fillUrl, setFillUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const sendLink = useSendQuestionnaireLink();

  // The modal stays mounted on list pages — refresh the prefill on each open.
  useEffect(() => {
    if (open) setEmail(sentTo ?? '');
  }, [open, sentTo]);

  function handleSend(withEmail: boolean) {
    if (!questionnaireId) return;
    sendLink.mutate(
      { id: questionnaireId, email: withEmail ? email.trim() || undefined : undefined },
      { onSuccess: (data) => setFillUrl(data.fillUrl) },
    );
  }

  function handleClose() {
    setFillUrl(null);
    setCopied(false);
    onClose();
  }

  return (
    <EntityModal open={open} onClose={handleClose} title="Send to Client" size="sm">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Clients fill the questionnaire online — no login needed. Every change is audited with
          device and location.
        </p>
        <div>
          <label htmlFor="send-email" className="block text-sm font-medium text-gray-700 mb-1">
            Client email (optional)
          </label>
          <input
            id="send-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="client@example.com"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleSend(true)}
            disabled={sendLink.isPending || !email.trim()}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300"
            data-testid="send-email-button"
          >
            <Mail size={14} />
            {sendLink.isPending ? 'Sending…' : 'Email Link'}
          </button>
          <button
            onClick={() => handleSend(false)}
            disabled={sendLink.isPending}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            data-testid="generate-link-button"
          >
            Generate Link Only
          </button>
        </div>
        {fillUrl && (
          <div className="p-3 bg-gray-50 rounded-lg space-y-2">
            <p className="text-xs text-gray-500">Share this link with your client:</p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={fillUrl}
                className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={() => {
                  void navigator.clipboard.writeText(fillUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                className="p-2 text-gray-500 hover:text-primary-600 border border-gray-200 rounded-lg"
                aria-label="Copy link"
                data-testid="copy-link-button"
              >
                {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        )}
      </div>
    </EntityModal>
  );
}
